import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquare, Trash2, AlertTriangle, CheckCircle, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function OuvidoriaEsquecimento() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    protocolo: "",
    email: "",
    confirmacao: ""
  });

  useEffect(() => {
    fetchEmpresa();
  }, [slug]);

  const fetchEmpresa = async () => {
    try {
      const res = await axios.get(`${API_URL}/empresas/${slug}/check`);
      if (res.data.exists) {
        setEmpresa(res.data);
      } else {
        navigate("/");
      }
    } catch (error) {
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.confirmacao !== "CONFIRMAR") {
      toast.error("Digite 'CONFIRMAR' para prosseguir");
      return;
    }

    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/empresas/${slug}/ouvidoria/esquecimento`, {
        data: {
          protocolo: formData.protocolo,
          email: formData.email,
          confirmacao: "CONFIRMAR"
        }
      });
      
      setSuccess(true);
      toast.success("Dados excluídos com sucesso");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao processar solicitação");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f5a623]"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
        <header className="bg-[#f5a623]">
          <div className="max-w-3xl mx-auto px-6 py-4">
            <Link to={`/${slug}`} className="inline-flex items-center gap-2 text-[#1a1a2e]/70 hover:text-[#1a1a2e] mb-4">
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Link>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-[#1a1a2e]" />
              <div>
                <h1 className="text-xl font-bold text-[#1a1a2e]">{empresa?.nome}</h1>
                <p className="text-sm text-[#1a1a2e]/70">Exclusão de Dados (LGPD)</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-[#1a1a2e] mb-4">Dados Excluídos</h2>
            <p className="text-slate-600 mb-6">
              Seus dados foram permanentemente removidos do sistema conforme a LGPD.
              Um email de confirmação foi enviado para o endereço cadastrado.
            </p>

            <Link to={`/${slug}`}>
              <Button className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e]">
                Voltar ao Canal
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-[#f5a623]">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to={`/${slug}`} className="inline-flex items-center gap-2 text-[#1a1a2e]/70 hover:text-[#1a1a2e] mb-4">
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Link>
          <div className="flex items-center gap-3">
            {empresa?.logo_url ? (
              <img 
                src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)} 
                alt="" 
                className="h-8" 
              />
            ) : (
              <MessageSquare className="w-8 h-8 text-[#1a1a2e]" />
            )}
            <div>
              <h1 className="text-xl font-bold text-[#1a1a2e]">{empresa?.nome}</h1>
              <p className="text-sm text-[#1a1a2e]/70">Direito ao Esquecimento (LGPD)</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-100 rounded-full">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-[#1a1a2e]">Solicitar Exclusão de Dados</h2>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-semibold mb-1">Atenção: Ação Irreversível</p>
                <p>Ao confirmar, todos os seus dados serão permanentemente excluídos:</p>
                <ul className="list-disc list-inside mt-2">
                  <li>Dados pessoais (nome, email, telefone)</li>
                  <li>Sua solicitação e descrição</li>
                  <li>Anexos enviados</li>
                  <li>Histórico de comunicação</li>
                </ul>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="protocolo">Número do Protocolo *</Label>
              <Input
                id="protocolo"
                value={formData.protocolo}
                onChange={(e) => setFormData(prev => ({ ...prev, protocolo: e.target.value.toUpperCase() }))}
                placeholder="Ex: OCON202602090001"
                required
                data-testid="ouvidoria-protocolo-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Email cadastrado *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="seu@email.com"
                required
                data-testid="ouvidoria-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                O mesmo email utilizado ao registrar a solicitação
              </p>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <Label htmlFor="confirmacao">
                Para confirmar, digite <span className="text-red-600 font-bold">CONFIRMAR</span>
              </Label>
              <Input
                id="confirmacao"
                value={formData.confirmacao}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmacao: e.target.value.toUpperCase() }))}
                placeholder="Digite CONFIRMAR"
                required
                data-testid="ouvidoria-confirm-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Link to={`/${slug}`} className="flex-1">
                <Button type="button" variant="outline" className="w-full" disabled={submitting}>
                  Cancelar
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={formData.confirmacao !== "CONFIRMAR" || submitting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="ouvidoria-delete-btn"
              >
                {submitting ? "Excluindo..." : "Excluir Meus Dados"}
              </Button>
            </div>
          </form>

          <p className="text-xs text-slate-400 mt-6 text-center">
            Conforme a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018)
          </p>
        </div>
      </main>
    </div>
  );
}
